// Vue SWIMLANES « Mes tâches » — grille LIGNES = logements (+ lignes spéciales
// Entreprise/Proprio/Divers) × COLONNES = statuts (DESIGN.md §8 ter). En-têtes
// de statut à pastille sémantique ; couloir de ligne avec icône de tête ;
// cellule = conteneur gris avec mini-cartes ; cellule vide = tiret discret.
// PRÉSENTATION SEULE — droppables, ids et sémantique du drag inchangés.

import { useDroppable } from '@dnd-kit/core'
import { Home, Building2, User, Pin } from 'lucide-react'
import { DS } from '../../lib/ui'
import TaskCard from './TaskCard'
import { STATUSES, STATUS_LABELS, cellKey } from './taches-logic'
import { STATUS_DOT, COLUMN_BG, stripLeadEmoji } from './taches-ui'

function rowIcon(row) {
  if (row.kind === 'logement') return <Home size={15} strokeWidth={2} />
  if (row.scope === 'entreprise') return <Building2 size={15} strokeWidth={2} />
  if (row.scope === 'proprio') return <User size={15} strokeWidth={2} />
  return <Pin size={15} strokeWidth={2} />
}

function Cell({ rowKey, status, tasks, cfg, labelsById, onEdit, onToggleDone }) {
  const id = cellKey(rowKey, status)
  const { setNodeRef, isOver } = useDroppable({ id })
  const dot = STATUS_DOT[status] || DS.ciel
  return (
    <div
      ref={setNodeRef}
      data-testid={`swimlane-cell-${id}`}
      style={{
        background: COLUMN_BG,
        border: isOver ? `2px dashed ${dot}` : '0.5px solid transparent',
        borderRadius: 10,
        padding: 8,
        minHeight: 64,
        transition: 'border-color .12s',
      }}
    >
      {tasks.length === 0 && (
        <div style={{ fontSize: 13, color: DS.textMuted, textAlign: 'center', padding: '14px 0' }}>—</div>
      )}
      {tasks.map(t => (
        <TaskCard
          key={t.id}
          task={t}
          cfg={cfg}
          labelsById={labelsById}
          onEdit={onEdit}
          onToggleDone={onToggleDone}
        />
      ))}
    </div>
  )
}

export default function SwimlaneGrid({ rows, grouped, cfg, labelsById, onEdit, onToggleDone }) {
  const gridTemplate = `minmax(140px, 200px) repeat(${STATUSES.length}, minmax(200px, 1fr))`

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, gap: 10, minWidth: 760 }}>
        {/* En-tête : cellule vide + statuts à pastille */}
        <div />
        {STATUSES.map(st => (
          <div
            key={st}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              padding: '4px 8px',
            }}
          >
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_DOT[st] || DS.ciel }} />
            <span style={{ fontFamily: DS.fontDisplay, fontSize: 14, fontWeight: 600, color: DS.marineDark }}>
              {STATUS_LABELS[st]}
            </span>
          </div>
        ))}

        {/* Une ligne par logement / scope spécial */}
        {rows.map(row => (
          <SwimlaneRow
            key={row.key}
            row={row}
            grouped={grouped}
            cfg={cfg}
            labelsById={labelsById}
            onEdit={onEdit}
            onToggleDone={onToggleDone}
          />
        ))}
      </div>
    </div>
  )
}

function SwimlaneRow({ row, grouped, cfg, labelsById, onEdit, onToggleDone }) {
  const special = row.kind === 'special'
  return (
    <>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: DS.fontBody, fontSize: 13, fontWeight: 600,
          color: special ? DS.marineDeep : DS.marineDark,
          background: special ? DS.cielSoft : '#eef3f9',
          borderRadius: 10, padding: '8px 10px',
          overflow: 'hidden',
        }}
        title={stripLeadEmoji(row.label)}
      >
        <span style={{ display: 'flex', color: DS.marine, flexShrink: 0 }}>{rowIcon(row)}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {stripLeadEmoji(row.label)}
        </span>
      </div>
      {STATUSES.map(st => (
        <Cell
          key={st}
          rowKey={row.key}
          status={st}
          tasks={grouped[cellKey(row.key, st)] || []}
          cfg={cfg}
          labelsById={labelsById}
          onEdit={onEdit}
          onToggleDone={onToggleDone}
        />
      ))}
    </>
  )
}
